const BASE_URL =
  process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") ??
  "http://localhost:8000";

export class HttpError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`HTTP ${status}`);
  }
}

type Options = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  token?: string | null;
  markets?: string[] | null;
};

/**
 * Module-level slot for a "session expired" listener. AuthProvider installs
 * it on mount; on a 401 from any authenticated endpoint request() invokes
 * it so the SPA can clear its token and bounce the user to /login without
 * each caller having to duplicate that logic.
 *
 * Singular by design: the SPA has a single auth context, so a single
 * handler keeps the "always idempotent" contract trivial — repeated 401s
 * from a burst of concurrent requests just call logout() multiple times
 * in a row, which is a no-op once the token is already cleared.
 */
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

// Login endpoint returns 401 when credentials are wrong — that's a user
// trying to start a new session, NOT an existing one expiring. Filter the
// auth path out so a typo on the login form doesn't trip the global handler.
const AUTH_PATH_PREFIX = "/api/admin/auth/";

export async function request<T>(path: string, opts: Options = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (opts.token) {
    headers["Authorization"] = `Bearer ${opts.token}`;
  }
  if (opts.markets && opts.markets.length > 0) {
    headers["X-Market"] = opts.markets.join(",");
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    if (response.status === 401 && !path.startsWith(AUTH_PATH_PREFIX)) {
      // Fire-and-forget: the handler is expected to be cheap (clear local
      // state) and the throw below still happens so callers' error branches
      // run their cleanup normally.
      unauthorizedHandler?.();
    }
    throw new HttpError(response.status, payload);
  }

  return payload as T;
}
